/**
 * Copyright 2018 Phenix Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
define([
    'phenix-web-lodash-light',
    'phenix-web-assert',
    'phenix-web-event',
    'phenix-web-disposable',
    './MQProtocol',
    './Base64'
], function(_, assert, event, disposable, MQProtocol, Base64) {
    'use strict';

    var clientRequestIdPrefix = 'C';
    var continuationStatus = 'continuation';
    var continuationPollInterval = 2000;
    var continuationTimeout = 300000;
    var defaultRequestTimeout = 18000;

    function MQService(logger, sendCallback, receiveCallback, protocols, apiVersion) {
        assert.isObject(logger, 'logger');
        assert.isFunction(sendCallback, 'sendCallback');
        assert.isFunction(receiveCallback, 'receiveCallback');

        this._logger = logger;
        this._mqProtocol = new MQProtocol(protocols, apiVersion);

        this._nextRequestId = 0;
        this._namedEvents = new event.NamedEvents();
        this._requests = {};
        this._responses = {};
        this._requestListeners = {};
        this._requestTimeouts = {};
        this._requestTimestamps = {};
        this._continuationTimeouts = {};

        this._receiveCallback = receiveCallback;
        this._sendCallback = sendCallback;

        receiveCallback(_.bind(onMessage, this));
    }

    MQService.prototype.onEvent = function(eventName, handler) {
        if (this._disposed) {
            return this._logger.warn('Proto already disposed of. Unable to listen for event');
        }

        assert.isStringNotEmpty(eventName, 'eventName');
        assert.isFunction(handler, 'handler');

        return this._namedEvents.listen(eventName, handler);
    };

    MQService.prototype.onRequest = function(requestName, handler) {
        if (this._disposed) {
            return this._logger.warn('Proto already disposed of. Unable to listen for request');
        }

        assert.isStringNotEmpty(requestName, 'requestName');
        assert.isFunction(handler, 'handler');

        if (this._requestListeners[requestName]) {
            this._logger.warn('May not set multiple handlers for the same request. Overriding existing handlers for request [%s]', requestName);
        }

        this._requestListeners[requestName] = handler;

        var that = this;

        return new disposable.Disposable(function() {
            that._requestListeners[requestName] = null;
        });
    };

    MQService.prototype.sendRequest = function sendRequest(type, message, callback, settings) {
        if (this._disposed) {
            this._logger.warn('Proto already disposed of. Unable to send request');

            return callback(null, {status: 'proto-disposed'});
        }

        settings = settings || {};

        assert.isStringNotEmpty(type, 'type');
        assert.isObject(message, 'message');
        assert.isObject(settings, 'settings');

        if (callback) {
            assert.isFunction(callback, 'callback');
        }

        if (settings.timeout) {
            assert.isNumber(settings.timeout, 'settings.timeout');
        }

        var requestId = clientRequestIdPrefix + (this._nextRequestId++).toString();
        var request = {
            requestId: requestId,
            type: type,
            payload: this._mqProtocol.encode(type, message),
            messageType: 'Request'
        };

        this._requests[requestId] = _.bind(handleResponseAndContinuations, this, requestId, type, message, settings.timeout, callback);

        return sendRequestWithTimeout.call(this, requestId, request, message, settings.timeout, callback);
    };

    MQService.prototype.sendResponse = function sendResponse(requestId, type, message, callback) {
        if (this._disposed) {
            this._logger.warn('Proto already disposed of. Unable to send response');

            return callback(null, {status: 'proto-disposed'});
        }

        assert.isStringNotEmpty(requestId, 'requestId');
        assert.isStringNotEmpty(type, 'type');
        assert.isObject(message, 'message');

        var start = this._responses[requestId];

        if (!start) {
            this._logger.error('Already responded to request [%s]', requestId);

            callback(new Error('no-response-handler'));
        }

        var response = {
            requestId: requestId,
            type: type,
            payload: this._mqProtocol.encode(type, message),
            messageType: 'Response',
            wallTime: [_.now() - start]
        };

        encodeAndSendMessage.call(this, response, message);

        delete this._responses[requestId];

        return callback(null, {status: 'ok'});
    };

    MQService.prototype.disposeOfRequests = function() {
        _.forOwn(this._requestTimeouts, function(timeout) {
            clearTimeout(timeout);
        });

        _.forOwn(this._continuationTimeouts, function(timeout) {
            clearTimeout(timeout);
        });

        this._requests = {};
        this._responses = {};
        this._requestListeners = {};
        this._requestTimeouts = {};
        this._requestTimestamps = {};
        this._continuationTimeouts = {};
    };

    MQService.prototype.dispose = function() {
        this._disposed = true;
        this._receiveCallback = null;
        this._sendCallback = null;

        this.disposeOfRequests();

        this._namedEvents.dispose();
    };

    MQService.prototype.getApiVersion = function getApiVersion() {
        return this._mqProtocol.getApiVersion();
    };

    MQService.prototype.toString = function() {
        return 'Proto[' + this._webSocket.toString() + ']';
    };

    function triggerEvent(eventName, args) {
        this._namedEvents.fire(eventName, args, this);
    }

    function onMessage(evt) {
        if (this._disposed) {
            return this._logger.warn('Proto already disposed of. Unable process message');
        }

        var message;
        var messageBody;
        var callback;

        try {
            message = this._mqProtocol.decode('mq.Message', Base64.toByteArray(evt.data));
            callback = this._requests[message.requestId];

            this._logger.info('>> [%s] [%s]', message.messageType, message.type);

            messageBody = this._mqProtocol.decode(message.type, message.payload);
        } catch (e) {
            this._logger.error(e);

            if (callback) {
                return callback(e);
            }

            throw e;
        }

        triggerReceivedEvent.call(this, message, messageBody, evt.data.length);

        switch (message.messageType) {
        case 'Response':
            if (messageBody.status !== continuationStatus) {
                delete this._requests[message.requestId];
            }

            clearTimeout(this._requestTimeouts[message.requestId]);

            delete this._requestTimeouts[message.requestId];

            if (!callback) {
                return;
            }

            if (message.type === 'mq.Error') {
                var error = messageBody;

                return callback(error, null);
            }

            return callback(null, messageBody);
        case 'Request':
            var sendResponse = _.bind(this.sendResponse, this, message.requestId, message.type + 'Response');
            var requestListener = this._requestListeners[message.type];

            this._responses[message.requestId] = _.now();

            if (requestListener) {
                requestListener(messageBody, sendResponse);
            } else {
                this._logger.info('Received request without a subscribed listener');
            }

            break;
        case 'Event':
            return triggerEvent.call(this, message.type, [messageBody]);
        default:
            return this._logger.warn('>> Unsupported message type [%s]', message.messageType);
        }
    }

    function handleResponseAndContinuations(requestId, type, message, timeout, callback, error, response) {
        if (response && response.status === continuationStatus) {
            if (!this._continuationTimeouts[requestId] && this._requests[requestId]) {
                this._continuationTimeouts[requestId] = listenForContinuationTimeout.call(this, requestId, callback);
            }

            return sendRequestWithContinuation.call(this, requestId, type, message, response, timeout, callback);
        }

        clearTimeout(this._continuationTimeouts[requestId]);

        delete this._continuationTimeouts[requestId];

        return callback(error, response);
    }

    function sendRequestWithContinuation(requestId, type, message, response, timeout, callback) {
        if (!response || (!response.continuationId && !response.routeKey)) {
            this._logger.warn('Received request for continuation without a continuationId or routeKey');

            return callback();
        }

        var messageWithContinuation = _.assign({}, message, {
            continuationId: response.continuationId,
            routeKey: response.routeKey
        });
        var request = {
            requestId: requestId,
            type: type,
            payload: this._mqProtocol.encode(type, messageWithContinuation),
            messageType: 'Request'
        };
        var that = this;

        return setTimeout(function() {
            if (!that._requests[requestId]) {
                return;
            }

            return sendRequestWithTimeout.call(that, requestId, request, messageWithContinuation, timeout, callback);
        }, continuationPollInterval);
    }

    function sendRequestWithTimeout(requestId, request, payload, timeout, callback) {
        if (this._requestTimeouts[requestId]) {
            clearTimeout(this._requestTimeouts[requestId]);
        }

        var requestTimeout = timeout || defaultRequestTimeout;

        this._requestTimeouts[requestId] = setTimeout(_.bind(handleRequestTimeout, this, requestId, requestTimeout, callback), requestTimeout);

        return encodeAndSendMessage.call(this, request, payload);
    }

    function encodeAndSendMessage(message, payload) {
        var encodedMessage = Base64.toString(this._mqProtocol.encode('mq.Message', message));

        triggerSentEvent.call(this, message, payload, encodedMessage.length);

        return this._sendCallback(encodedMessage);
    }

    function listenForContinuationTimeout(requestId, callback) {
        return setTimeout(_.bind(handleRequestTimeout, this, requestId, continuationTimeout, callback), continuationTimeout);
    }

    function handleRequestTimeout(requestId, timeout, callback) {
        if (!this._requests[requestId]) {
            return;
        }

        triggerTimeoutEvent.call(this, requestId, timeout);

        this._logger.warn('Request [%s] has not completed in [%s] ms. Dropping request.', requestId, timeout);

        delete this._requests[requestId];

        return callback(null, {status: 'timeout'});
    }

    function triggerReceivedEvent(message, payload, bytes) {
        var now = _.now();
        var sentTimestamp = this._requestTimestamps[message.requestId];

        if (_.isNumber(sentTimestamp)) {
            var totalRequestTime = now - sentTimestamp;

            message.wallTime = [totalRequestTime].concat(message.wallTime);
        }

        this._namedEvents.fireAsync('received', [{
            key: message.requestId,
            messageType: message.messageType,
            type: message.type,
            wallTime: message.wallTime,
            received: now,
            bytes: bytes,
            payload: payload
        }]);

        delete this._requestTimestamps[message.requestId];
    }

    function triggerSentEvent(message, payload, bytes) {
        var now = _.now();

        this._requestTimestamps[message.requestId] = now;

        this._namedEvents.fireAsync('sent', [{
            key: message.requestId,
            messageType: message.messageType,
            type: message.type,
            received: now,
            bytes: bytes,
            payload: payload
        }]);
    }

    function triggerTimeoutEvent(requestId, timeout) {
        this._namedEvents.fireAsync('timeout', [{
            key: requestId,
            timeout: timeout
        }]);

        delete this._requestTimestamps[requestId];
    }

    return MQService;
});